import SvgPath from 'svgpath'
import { gridPath, ICON_GRID } from '../utils/svgIcon'

/**
 * Layer maths for the icon studio. Everything is on the 24 unit grid, y down.
 *
 * A layer is { id, type: 'icon' | 'shape' | 'path', code | shape | d, x, y, size, rotate, flipX,
 * flipY, mode: 'add' | 'cut', tone: 'solid' | 'light', cutout: 'none' | 'circle' |
 * 'square', gap }: the source is drawn full size (24 units), scaled by `size` and
 * turned about the grid's centre, then moved by x / y. A 'cut' layer isn't drawn: it
 * punches its own shape out of everything below. A cut-out clears a gap around the
 * layer in everything below it, the way badges sit on icons.
 */

const C = ICON_GRID / 2

const circle = (cx, cy, r) =>
	`M${cx - r} ${cy}a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 ${-r * 2} 0z`

// Clockwise outer edge; the inner edge runs the other way, so it reads as a hole
const roundedRect = (x, y, w, h, r, reverse = false) => {
	r = Math.max(0, Math.min(r, w / 2, h / 2))
	return reverse
		? `M${x + r} ${y}a${r} ${r} 0 0 0 ${-r} ${r}v${h - r * 2}a${r} ${r} 0 0 0 ${r} ${r}h${w - r * 2}a${r} ${r} 0 0 0 ${r} ${-r}v${-(h - r * 2)}a${r} ${r} 0 0 0 ${-r} ${-r}z`
		: `M${x + r} ${y}h${w - r * 2}a${r} ${r} 0 0 1 ${r} ${r}v${h - r * 2}a${r} ${r} 0 0 1 ${-r} ${r}h${-(w - r * 2)}a${r} ${r} 0 0 1 ${-r} ${-r}v${-(h - r * 2)}a${r} ${r} 0 0 1 ${r} ${-r}z`
}

const reversedCircle = (cx, cy, r) =>
	`M${cx - r} ${cy}a${r} ${r} 0 1 1 ${r * 2} 0a${r} ${r} 0 1 1 ${-r * 2} 0z`

// Basic shapes, drawn in the 20 unit live area (2 units of padding, like the icons)
export const SHAPES = {
	circle: { label: 'Circle', d: circle(C, C, 10) },
	ring: { label: 'Ring', d: circle(C, C, 10) + reversedCircle(C, C, 8) },
	square: { label: 'Square', d: roundedRect(2, 2, 20, 20, 0) },
	rounded: { label: 'Rounded', d: roundedRect(2, 2, 20, 20, 5) },
	frame: { label: 'Frame', d: roundedRect(2, 2, 20, 20, 5) + roundedRect(4, 4, 16, 16, 3, true) }
}

export const newLayer = (props) => ({
	id: Math.random().toString(36).slice(2, 10),
	x: 0,
	y: 0,
	size: 1,
	rotate: 0,
	flipX: false,
	flipY: false,
	mode: 'add',
	tone: 'solid',
	cutout: 'none',
	gap: 1.5,
	...props
})

export const transformPath = (d, layer) =>
	new SvgPath(d)
		.translate(-C, -C)
		.scale(layer.size * (layer.flipX ? -1 : 1), layer.size * (layer.flipY ? -1 : 1))
		.rotate(layer.rotate)
		.translate(C + layer.x, C + layer.y)
		.abs()
		.round(3)
		.toString()

/**
 * The layer's drawn parts: [{ d, tone }]. A duotone icon's second layer is always light.
 * @param {Object} layer
 * @param {Object} lookup - { byCode: { code: icon }, secondaries: { name: icon } }
 */
export function layerParts(layer, lookup) {
	// A part split off an icon: its own path, kept centred on the grid so it turns and
	// scales about its own middle
	if (layer.type === 'path') return layer.d ? [{ d: transformPath(layer.d, layer), tone: layer.tone }] : []
	if (layer.type === 'shape') {
		const shape = SHAPES[layer.shape]
		return shape ? [{ d: transformPath(shape.d, layer), tone: layer.tone }] : []
	}
	const icon = lookup.byCode[layer.code]
	if (!icon || !icon.svg) return []
	const parts = [{ d: transformPath(gridPath(icon.svg), layer), tone: layer.tone }]
	const secondary = icon.isDuotone ? lookup.secondaries[icon.name] : null
	if (secondary) parts.push({ d: transformPath(gridPath(secondary.svg), layer), tone: 'light' })
	return parts
}

/** Bounding box from the path's points (control points included, so a little generous) */
export function pathBox(d) {
	let minX = Infinity
	let minY = Infinity
	let maxX = -Infinity
	let maxY = -Infinity
	const add = (x, y) => {
		if (x < minX) minX = x
		if (x > maxX) maxX = x
		if (y < minY) minY = y
		if (y > maxY) maxY = y
	}
	new SvgPath(d)
		.abs()
		.unarc()
		.unshort()
		.iterate((segment, index, x, y) => {
			const [cmd, ...args] = segment
			if (cmd === 'H') add(args[0], y)
			else if (cmd === 'V') add(x, args[0])
			else for (let i = 0; i + 1 < args.length; i += 2) add(args[i], args[i + 1])
		})
	if (minX === Infinity) return null
	return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
}

export function layerBox(parts) {
	const boxes = parts.map((part) => pathBox(part.d)).filter(Boolean)
	if (!boxes.length) return null
	const x = Math.min(...boxes.map((b) => b.x))
	const y = Math.min(...boxes.map((b) => b.y))
	return {
		x,
		y,
		w: Math.max(...boxes.map((b) => b.x + b.w)) - x,
		h: Math.max(...boxes.map((b) => b.y + b.h)) - y
	}
}

/** The area a cut-out clears: a circle or rounded square around the layer, plus the gap */
export function haloPath(layer, box) {
	if (!box || layer.mode === 'cut' || layer.cutout === 'none') return null
	const cx = box.x + box.w / 2
	const cy = box.y + box.h / 2
	if (layer.cutout === 'circle') return circle(cx, cy, Math.max(box.w, box.h) / 2 + layer.gap)
	const w = box.w + layer.gap * 2
	const h = box.h + layer.gap * 2
	return roundedRect(cx - w / 2, cy - h / 2, w, h, Math.min(w, h) * 0.22)
}

/**
 * Everything the canvas draws, bottom to top: each layer's parts and box, and the
 * cut-outs from the layers above it that clear part of it
 */
export function describe(layers, lookup) {
	const drawn = layers.map((layer) => {
		const parts = layerParts(layer, lookup)
		const box = layerBox(parts)
		const halo = haloPath(layer, box)
		// What this layer clears below it: the gap around it, or all of it for a hole
		const cuts = layer.mode === 'cut' ? parts.map((part) => part.d) : halo ? [halo] : []
		return { layer, parts, box, halo, cuts }
	})
	return drawn.map((item, index) => ({
		...item,
		cutBy: [].concat(...drawn.slice(index + 1).map((above) => above.cuts))
	}))
}

const tidy = (d) => (d ? new SvgPath(d).rel().round(2).toString() : '')

/**
 * Merge the design into one solid path and one light (60%) path with paper.js booleans,
 * for exports and fonts. The light path never overlaps the solid one.
 * @param {Array} described - describe() output
 * @param {Object} paper - paper.js, set up (see loadPaper)
 * @return {{ solid: string, light: string }}
 */
export function flatten(described, paper) {
	const item = (d) => new paper.CompoundPath({ pathData: d, insert: false, fillRule: 'nonzero' })
	let solid = null
	let light = null
	described.forEach(({ layer, parts, cuts }) => {
		cuts.forEach((d) => {
			const cut = item(d)
			if (solid) solid = solid.subtract(cut, { insert: false })
			if (light) light = light.subtract(cut, { insert: false })
		})
		if (layer.mode === 'cut') return
		parts.forEach((part) => {
			const shape = item(part.d)
			if (part.tone === 'light') light = light ? light.unite(shape, { insert: false }) : shape
			else solid = solid ? solid.unite(shape, { insert: false }) : shape
		})
	})
	if (solid && light) light = light.subtract(solid, { insert: false })
	return { solid: tidy(solid && solid.pathData), light: tidy(light && light.pathData) }
}

/**
 * Break a layer into its separate shapes, each with the holes inside it (the counter of
 * an "o", the gap in a ring), as new path layers that sit exactly where the parts were
 * @param {Object} item - one entry of describe()
 * @param {Object} paper - paper.js, set up (see loadPaper)
 * @return {Array} layers, bottom to top
 */
export function splitLayer(item, paper, label) {
	const pieces = []
	item.parts.forEach((part) => {
		const compound = new paper.CompoundPath({ pathData: part.d, insert: false, fillRule: 'nonzero' })
		const contours = compound.children.map((path) => ({ path, area: Math.abs(path.area) }))
		// How deep each contour sits inside the others: even depth is a shape, odd a hole
		contours.forEach((contour) => {
			const point = contour.path.interiorPoint
			contour.parents = contours.filter(
				(other) =>
					other !== contour &&
					other.area > contour.area &&
					other.path.bounds.contains(contour.path.bounds) &&
					other.path.contains(point)
			)
		})
		contours
			.filter((contour) => contour.parents.length % 2 === 0)
			.forEach((outer) => {
				const holes = contours.filter(
					(hole) => hole.parents.length === outer.parents.length + 1 && hole.parents.indexOf(outer) !== -1
				)
				pieces.push({ d: [outer, ...holes].map((contour) => contour.path.pathData).join(''), tone: part.tone })
			})
	})
	return pieces.map((piece, index) => {
		const box = pathBox(piece.d)
		const cx = box.x + box.w / 2
		const cy = box.y + box.h / 2
		return newLayer({
			type: 'path',
			d: new SvgPath(piece.d).translate(C - cx, C - cy).abs().round(3).toString(),
			label: `${label} ${index + 1}`,
			x: Math.round((cx - C) * 1000) / 1000,
			y: Math.round((cy - C) * 1000) / 1000,
			tone: piece.tone,
			mode: item.layer.mode
		})
	})
}

/**
 * Move, scale, turn or flip some layers together about `origin`, as one rigid shape.
 * Each layer turns about its own pivot (the grid centre plus its x / y), so moving the
 * pivots about the origin and adjusting each layer's own size / angle / flip is enough.
 * @param {Array} layers - all layers
 * @param {Array} ids - the ones to change
 * @param {Object} op - { move: [dx, dy] } | { scale: k } | { rotate: degrees } | { flip: 'x' | 'y' }
 * @param {Object} origin - { x, y } on the grid
 */
export function transformLayers(layers, ids, op, origin = { x: C, y: C }) {
	const round3 = (n) => Math.round(n * 1000) / 1000
	return layers.map((layer) => {
		if (ids.indexOf(layer.id) === -1) return layer
		const px = C + layer.x
		const py = C + layer.y
		let next = {}
		if (op.move) {
			next = { x: layer.x + op.move[0], y: layer.y + op.move[1] }
		} else if (op.scale) {
			next = {
				x: origin.x + (px - origin.x) * op.scale - C,
				y: origin.y + (py - origin.y) * op.scale - C,
				size: layer.size * op.scale
			}
		} else if (op.rotate) {
			const a = (op.rotate * Math.PI) / 180
			const dx = px - origin.x
			const dy = py - origin.y
			next = {
				x: origin.x + dx * Math.cos(a) - dy * Math.sin(a) - C,
				y: origin.y + dx * Math.sin(a) + dy * Math.cos(a) - C,
				rotate: (((layer.rotate + op.rotate) % 360) + 360) % 360
			}
		} else if (op.flip === 'x') {
			// A mirror swaps the turn's direction: M·R(θ) = R(−θ)·M
			next = { x: 2 * origin.x - px - C, flipX: !layer.flipX, rotate: (360 - layer.rotate) % 360 }
		} else if (op.flip === 'y') {
			next = { y: 2 * origin.y - py - C, flipY: !layer.flipY, rotate: (360 - layer.rotate) % 360 }
		}
		if (next.x !== undefined) next.x = round3(next.x)
		if (next.y !== undefined) next.y = round3(next.y)
		if (next.size !== undefined) next.size = round3(next.size)
		return { ...layer, ...next }
	})
}

/** The box around several described layers */
export function unionBox(items) {
	const boxes = items.map((item) => item.box).filter(Boolean)
	if (!boxes.length) return null
	const x = Math.min(...boxes.map((b) => b.x))
	const y = Math.min(...boxes.map((b) => b.y))
	return {
		x,
		y,
		w: Math.max(...boxes.map((b) => b.x + b.w)) - x,
		h: Math.max(...boxes.map((b) => b.y + b.h)) - y
	}
}
